import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CategoryApiV2 from '../../lib/api/category/CategoryApiV2';
import toastMsg from '../../lib/toastMsg';
import downloadBlobFile from '../../lib/utils/common/downloadBlobFile';
import {
  clearBulkJobRecord,
  extractDuplicatedJobId,
  isBulkJobPollingDone,
  isBulkJobSettled,
  readBulkJobRecord,
  resolveBulkUploadErrorMessage,
  resolveBulkUploadHttpErrorMessage,
  writeBulkJobRecord,
} from '../../lib/utils/productCategoryLinkBulkUtils';
import { CATEGORY } from '../../constants/queryKeys';
import {
  PRODUCT_CATEGORY_LINK_BULK_ERROR_CODE,
  PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT,
  PRODUCT_CATEGORY_LINK_BULK_MAX_FILE_SIZE_BYTES,
} from '../../constants/service/app/productCategoryLinkBulkConstants';
import {
  BULK_UPLOAD_JOB_EXPIRED,
  BULK_UPLOAD_JOB_STATUS,
  BulkUploadJobQueryData,
} from '../../types/category/bulkUpload';
import { useAuth } from '../common/useAuth';
import { useProductCategoryLinkBulkUploadMutation } from '../queries/category/useProductCategoryLinkBulkUploadMutation';
import { useProductCategoryLinkBulkJobQuery } from '../queries/category/useProductCategoryLinkBulkJobQuery';

export type ProductCategoryLinkBulkJobFeature = {
  /** 진행 중인 잡 (없으면 null) */
  jobId: string | null;
  /** 업로드한 파일명 — 카드에 표시 */
  fileName: string;
  /** 파일 전송 중일 때의 전송률(0~100). 전송이 끝나면 null */
  uploadPercent: number | null;
  /** 폴링으로 받은 잡 상태 */
  job: BulkUploadJobQueryData | undefined;
  /** 잡 상태를 한 번도 받지 못한 채 조회가 실패한 상태 (서버 장애 등) */
  isStatusUnavailable: boolean;
  /** 업로드 버튼을 비활성화해야 하는 상태 (전송 중 또는 처리 중) */
  isBusy: boolean;
  isDownloadingFailedCsv: boolean;
  /** 업로드 시작 — 사전 검증에서 걸러지면 false */
  upload: (file: File) => boolean;
  downloadFailedCsv: () => void;
  /** 완료·실패 카드 닫기 */
  dismiss: () => void;
};

/**
 * 상품-카테고리 링크 CSV 벌크 업로드 오케스트레이션.
 *
 * 업로드 → 잡 폴링 → 종결 처리 → 실패 행 CSV 다운로드를 한곳에서 다룬다.
 * 진행 중 jobId 는 localStorage 에 남겨 페이지를 떠나도 복원할 수 있게 한다.
 */
const useProductCategoryLinkBulkJob = (): ProductCategoryLinkBulkJobFeature => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const adminId = user?.id ?? null;

  const [jobId, setJobId] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number>(() => Date.now());
  const [fileName, setFileName] = useState<string>('');
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  /**
   * 폴링 완전 종료 여부 (창 포커스 refetch 까지 함께 막는다).
   * 메인 잡 종결이 아니라 isBulkJobPollingDone 기준 — 완료 후에도 ES 동기화가
   * 진행 중이면 완료 카드의 수치를 갱신해야 하므로 폴링을 이어간다.
   */
  const [pollingStopped, setPollingStopped] = useState(false);

  /** 어드민 프로필이 로드된 뒤 한 번만 복원한다 */
  const restoredRef = useRef(false);
  /** 같은 종결 상태를 두 번 처리하지 않도록 기록 */
  const settledHandledRef = useRef<string | null>(null);

  const startWatching = useCallback((nextJobId: string, nextFileName: string, nextStartedAt: number) => {
    setJobId(nextJobId);
    setFileName(nextFileName);
    setStartedAt(nextStartedAt);
    setPollingStopped(false);
    settledHandledRef.current = null;
  }, []);

  // 페이지 재진입·새로고침 복원.
  // useAuth().user 는 프로필 쿼리 결과라 첫 렌더에는 없다 — adminId 가 채워진 뒤 실행한다.
  useEffect(() => {
    if (adminId === null || restoredRef.current) {
      return;
    }
    restoredRef.current = true;

    const record = readBulkJobRecord(adminId);
    if (record) {
      startWatching(record.jobId, record.fileName, record.startedAt);
    }
  }, [adminId, startWatching]);

  const jobQuery = useProductCategoryLinkBulkJobQuery({
    jobId,
    startedAt,
    // 종결 후에는 폴링을 끊는다. jobId 는 그대로 넘겨야 쿼리 키가 유지돼
    // 마지막 응답(완료 결과)이 캐시에 남는다.
    enabled: !pollingStopped,
  });

  const job = jobQuery.data;

  /**
   * 상태를 한 번도 받지 못한 채 조회가 실패한 경우.
   * 이때 잡을 진행 중으로 취급하면 업로드 버튼이 잠긴 채로 빠져나올 수 없다
   * (새로고침해도 저장 기록에서 같은 잡이 복원된다).
   */
  const isStatusUnavailable = jobQuery.isError && !job;

  const uploadMutation = useProductCategoryLinkBulkUploadMutation({
    // 옵션 레벨 콜백이라 전송 중 페이지를 떠나도 실행된다 (mutate 호출부 콜백은 실행되지 않는다)
    onSuccess: (data, variables) => {
      setUploadPercent(null);

      /**
       * 백엔드 피처 플래그(AMZBM-29) OFF 방어. flag OFF 응답은 201 + productCategoryLinks
       * 로 jobId 가 없다 — 그대로 진행하면 카드도 안 뜨고 폴링도 없이 조용히 끝난다.
       * (CSV 는 대개 동기 경로의 xlsx 검증에서 400 으로 걸리지만, 계약이 어긋난 2xx 도 막는다)
       */
      if (!data?.jobId) {
        toastMsg.error('업로드 응답에 작업 ID가 없습니다. 서버 설정이 아직 준비되지 않았을 수 있으니 운영팀에 문의해주세요.');
        return;
      }

      if (adminId !== null) {
        writeBulkJobRecord(adminId, { jobId: data.jobId, fileName: variables.file.name, startedAt: Date.now() });
      }
      startWatching(data.jobId, variables.file.name, Date.now());
    },
    onError: (error, variables) => {
      setUploadPercent(null);

      // 중복 차단(409). 마커는 어드민 단위라 진행 중 잡도 본인 것 — 그 jobId 로 진행 카드를 복원한다.
      const duplicatedJobId = extractDuplicatedJobId(error);
      if (duplicatedJobId) {
        toastMsg.info(PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT.DUPLICATED);

        if (adminId !== null) {
          writeBulkJobRecord(adminId, {
            jobId: duplicatedJobId,
            fileName: variables.file.name,
            startedAt: Date.now(),
          });
        }
        startWatching(duplicatedJobId, variables.file.name, Date.now());
        return;
      }

      // 서버 필터가 한글 메시지를 완비하므로 서버 메시지 우선 (코드 매핑은 폴백)
      toastMsg.error(resolveBulkUploadHttpErrorMessage(error), error);
    },
  });

  // 폴링 종료 — ES 동기화까지 끝났을 때(또는 실패·만료)만 멈춘다.
  // 저장 기록도 이 시점에 지운다. 메인 잡 완료 직후 지우면 ES 반영 중 새로고침했을 때
  // 카드를 복원할 수 없어 남은 검색 인덱스 진행을 볼 방법이 없다.
  useEffect(() => {
    if (isBulkJobPollingDone(job)) {
      setPollingStopped(true);

      if (adminId !== null) {
        clearBulkJobRecord(adminId);
      }
    }
  }, [adminId, job]);

  // 종결 처리(1회) — 완료 시 카테고리 리스트 갱신·토스트, 만료 안내.
  // 메인 잡 종결 기준이다: 이 시점에 백엔드 중복 차단 마커가 해제되므로 버튼도 함께 풀린다.
  // (저장 기록 정리는 ES 동기화까지 끝난 뒤 — 위 폴링 종료 effect 담당)
  useEffect(() => {
    if (!jobId || !isBulkJobSettled(job)) {
      return;
    }

    const settledKey = `${jobId}:${job?.status}`;
    if (settledHandledRef.current === settledKey) {
      return;
    }
    settledHandledRef.current = settledKey;

    if (job?.status === BULK_UPLOAD_JOB_EXPIRED) {
      toastMsg.info(PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT.EXPIRED);
      setJobId(null);
      return;
    }

    /**
     * 종결 토스트 — 카드는 수동 확인용이라, 다른 작업을 하다 돌아온 어드민에게
     * 끝났다는 신호를 준다 (기존 동기 흐름도 완료 토스트를 띄웠다).
     * 페이지를 떠나 있으면 이 훅이 언마운트라 토스트가 안 뜨는 것은 알려진 한계.
     */
    if (job?.status === BULK_UPLOAD_JOB_STATUS.COMPLETED) {
      queryClient.invalidateQueries([...CATEGORY.LIST]);
      toastMsg.success('상품 일괄 업로드가 완료되었습니다.');
    } else if (job?.status === BULK_UPLOAD_JOB_STATUS.FAILED) {
      toastMsg.error('상품 일괄 업로드에 실패했습니다. 사유는 진행 카드에서 확인해주세요.');
    }
  }, [adminId, job, jobId, queryClient]);

  const downloadFailedCsvMutation = useMutation({
    mutationFn: (targetJobId: string) => CategoryApiV2.downloadProductCategoryLinkBulkFailedCsv(targetJobId),
    onSuccess: (blob, targetJobId) => {
      // 응답 인터셉터가 res.data 만 돌려줘 Content-Disposition 을 읽을 수 없다 — 파일명을 직접 만든다
      downloadBlobFile(blob, `failed-rows-${targetJobId}.csv`);
    },
    onError: (error) => {
      // 에러 바디가 JSON 이 아니라 Blob 이라 파싱하지 않고 고정 문구로 알린다
      toastMsg.error('실패 행 CSV 를 다운로드하지 못했습니다.', error);
    },
  });

  /**
   * 업로드 시작. 사전 검증에서 걸러지면 false 를 반환한다.
   * 호출부가 이 값으로 모달을 닫을지 판단해, 거절된 경우 사용자가 파일만 바꿔 다시 시도할 수 있다.
   */
  const upload = (file: File): boolean => {
    // 전송이 이미 진행 중이면 무시한다 — 모달 확인 버튼에는 비활성 처리가 없어
    // 빠른 연속 클릭이 POST 를 두 번 만들 수 있다
    if (uploadMutation.isLoading) {
      return false;
    }

    /**
     * 진행 중 잡이 있으면 차단한다. 버튼은 isBusy 로 잠기지만, 모달이 열린 사이
     * localStorage 복원이 도착하는 레이스에서는 여기까지 올 수 있다 — 그대로 보내면
     * 409 복원 경로가 진행 중 카드의 파일명을 실패한 시도의 것으로 덮어쓴다.
     */
    if (jobId !== null && !isStatusUnavailable && !isBulkJobSettled(job)) {
      toastMsg.info(PRODUCT_CATEGORY_LINK_BULK_GUIDE_TEXT.DUPLICATED);
      return false;
    }

    if (file.size > PRODUCT_CATEGORY_LINK_BULK_MAX_FILE_SIZE_BYTES) {
      // 용량 초과는 전송 전에 막아 큰 파일을 보내고 400 을 받는 왕복을 없앤다
      toastMsg.error(resolveBulkUploadErrorMessage(PRODUCT_CATEGORY_LINK_BULK_ERROR_CODE.FILE_SIZE_EXCEEDED));
      return false;
    }

    // 이전 잡의 결과 카드는 새 업로드를 시작하는 순간 버린다.
    // 그대로 두면 이번 업로드가 실패했을 때 지난 완료 카드가 되살아나 오해를 준다.
    if (isBulkJobSettled(job)) {
      setJobId(null);
    }

    setFileName(file.name);
    setUploadPercent(0);
    uploadMutation.mutate({ file, onUploadProgress: setUploadPercent });

    return true;
  };

  const downloadFailedCsv = () => {
    if (jobId) {
      downloadFailedCsvMutation.mutate(jobId);
    }
  };

  const dismiss = () => {
    /**
     * 닫기는 종결된 잡과 상태를 확인할 수 없는 잡에만 제공된다(진행 중 카드에는 닫기가 없다).
     * 저장 기록까지 지워야 상태 조회가 계속 실패하는 잡을 새로고침 때마다 다시 복원하지 않는다.
     */
    if (adminId !== null) {
      clearBulkJobRecord(adminId);
    }

    setJobId(null);
    setFileName('');
  };

  const isBusy = uploadPercent !== null || (!!jobId && !isBulkJobSettled(job) && !isStatusUnavailable);

  return {
    jobId,
    fileName,
    uploadPercent,
    job,
    isStatusUnavailable,
    isBusy,
    isDownloadingFailedCsv: downloadFailedCsvMutation.isLoading,
    upload,
    downloadFailedCsv,
    dismiss,
  };
};

export default useProductCategoryLinkBulkJob;
